"use client";

import * as React from "react";

import { Transition } from "@headlessui/react";
import { cva, type VariantProps } from "class-variance-authority";

import { cn } from "@/shared/helpers/cn.helper";

const sideVariants = cva("pointer-events-auto absolute top-4 z-30", {
  variants: {
    side: {
      left: "left-4",
      right: "right-4",
    },
  },
  defaultVariants: {
    side: "left",
  },
});

export interface SideModalProps
  extends React.HTMLAttributes<HTMLDivElement>,
    VariantProps<typeof sideVariants> {
  children: React.ReactNode;
  show?: boolean;
  widthClassName?: string;
  contentStyle?: React.CSSProperties;
  onResizeMouseDown?: (e: React.MouseEvent) => void;
}

export function SideModal({
  children,
  show = true,
  side = "left",
  widthClassName = "w-[360px]",
  contentStyle,
  onResizeMouseDown,
  className,
  ...props
}: SideModalProps) {
  const isLeft = side === "left";

  return (
    <Transition
      enter="transition-all duration-200 ease-out"
      enterFrom={isLeft ? "-translate-x-4 opacity-0" : "translate-x-4 opacity-0"}
      enterTo="translate-x-0 opacity-100"
      leave="transition-all duration-200 ease-in"
      leaveFrom="translate-x-0 opacity-100"
      leaveTo={isLeft ? "-translate-x-4 opacity-0" : "translate-x-4 opacity-0"}
      show={show}
    >
      <div
        className={cn(
          sideVariants({ side }),
          className,
        )}
        {...props}
      >
        <div
          className={cn(
            "relative overflow-hidden border-light-100 h-full rounded-xl border bg-white p-2.5 shadow-sm",
            !contentStyle && widthClassName,
          )}
          style={contentStyle}
        >
          {onResizeMouseDown && (
            <div
              className="absolute bottom-0 right-0 top-0 z-10 w-1 cursor-col-resize hover:bg-gray-300/60 active:bg-gray-400/60"
              onMouseDown={onResizeMouseDown}
            />
          )}
          {children}
        </div>
      </div>
    </Transition>
  );
}

